import { getApiBaseUrl, SALON_API_PREFIX } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';
import {
  SALON_APP_SESSION_TOKEN_KEY,
  SALON_LOGIN_DEVICE_ID_KEY,
  SALON_LOGIN_EXPIRES_AT_KEY,
  SALON_LOGIN_FLAG_KEY,
  SALON_LOGIN_SESSION_TOKEN_KEY,
  SALON_LOGIN_USERNAME_KEY,
  SALON_LAST_ACTIVITY_AT_KEY,
  SALON_LOGIN_TIMEOUT_MINUTES_KEY,
  SALON_LOGIN_SERVER_VALIDATED_KEY,
  SALON_ORGANIZATION_ID_KEY,
  SALON_ORG_PLAN_KEY,
} from '../config/salonApp';
import {
  applyServerSessionExpiry,
  isSalonSessionIdleExpired,
  recordSalonUserActivity,
  setSalonLoginTimeoutMinutes,
} from './salonSessionIdle';

export {
  recordSalonUserActivity,
  setSalonLoginTimeoutMinutes,
  applyServerSessionExpiry,
  isSalonSessionIdleExpired,
  getSalonIdleTimeoutMs,
} from './salonSessionIdle';

export function shouldClearSalonSessionOnValidateFailure(result) {
  if (!result || result.ok) return false;
  if (result.code === 'VALIDATE_FAILED') return false;
  return true;
}

export function hasActiveSalonLogin() {
  try {
    const loggedIn = sessionStorage.getItem(SALON_LOGIN_FLAG_KEY) === '1';
    const loginSessionToken = String(sessionStorage.getItem(SALON_LOGIN_SESSION_TOKEN_KEY) || '').trim();
    if (!loggedIn || !loginSessionToken) return false;
    if (isSalonSessionIdleExpired()) return false;
    return true;
  } catch (_) {
    return false;
  }
}

export function clearSalonAuthStorage() {
  try {
    sessionStorage.removeItem(SALON_LOGIN_FLAG_KEY);
    sessionStorage.removeItem(SALON_LOGIN_SESSION_TOKEN_KEY);
    sessionStorage.removeItem(SALON_LOGIN_USERNAME_KEY);
    sessionStorage.removeItem(SALON_LOGIN_EXPIRES_AT_KEY);
    sessionStorage.removeItem(SALON_LAST_ACTIVITY_AT_KEY);
    sessionStorage.removeItem(SALON_LOGIN_TIMEOUT_MINUTES_KEY);
    sessionStorage.removeItem(SALON_LOGIN_DEVICE_ID_KEY);
    sessionStorage.removeItem(SALON_ORGANIZATION_ID_KEY);
    sessionStorage.removeItem(SALON_ORG_PLAN_KEY);
    sessionStorage.removeItem(SALON_APP_SESSION_TOKEN_KEY);
    sessionStorage.removeItem(SALON_LOGIN_SERVER_VALIDATED_KEY);
  } catch (_) {}
}

function currentLoginSessionToken() {
  try {
    return String(sessionStorage.getItem(SALON_LOGIN_SESSION_TOKEN_KEY) || '').trim();
  } catch (_) {
    return '';
  }
}

export function isSalonLoginServerValidated() {
  const token = currentLoginSessionToken();
  if (!token) return false;
  try {
    return sessionStorage.getItem(SALON_LOGIN_SERVER_VALIDATED_KEY) === token;
  } catch (_) {
    return false;
  }
}

export function markSalonLoginServerValidated() {
  const token = currentLoginSessionToken();
  if (!token) return;
  try {
    sessionStorage.setItem(SALON_LOGIN_SERVER_VALIDATED_KEY, token);
  } catch (_) {}
}

export function clearSalonLoginServerValidated() {
  try {
    sessionStorage.removeItem(SALON_LOGIN_SERVER_VALIDATED_KEY);
  } catch (_) {}
}

let _validateInFlight = null;

/**
 * Call POST /auth/validate at most once per login session (deduped in-flight).
 * @param {{ force?: boolean }} [options] - force=true always hits the server
 */
export async function ensureSalonLoginValidated(options = {}) {
  const { force = false } = options;

  if (!hasActiveSalonLogin()) {
    return { ok: false, code: 'MISSING_SESSION' };
  }

  if (!force && isSalonLoginServerValidated()) {
    return { ok: true, cached: true };
  }

  if (_validateInFlight) {
    return _validateInFlight;
  }

  _validateInFlight = validateSalonLoginWithServer()
    .then((result) => {
      if (result.ok) {
        markSalonLoginServerValidated();
      }
      return result;
    })
    .finally(() => {
      _validateInFlight = null;
    });

  return _validateInFlight;
}

export function clearSalonStorageScopes() {
  const scopedPrefixes = ['salon_', 'saloon_'];
  const scopedContains = ['_salon_'];

  try {
    const sessionKeys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k) sessionKeys.push(k);
    }
    sessionKeys.forEach((k) => {
      if (scopedPrefixes.some((p) => k.startsWith(p)) || scopedContains.some((c) => k.includes(c))) {
        sessionStorage.removeItem(k);
      }
    });
  } catch (_) {}

  try {
    const localKeys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k) localKeys.push(k);
    }
    localKeys.forEach((k) => {
      if (scopedPrefixes.some((p) => k.startsWith(p)) || scopedContains.some((c) => k.includes(c))) {
        localStorage.removeItem(k);
      }
    });
  } catch (_) {}
}

function readSalonDeviceId() {
  try {
    return String(sessionStorage.getItem(SALON_LOGIN_DEVICE_ID_KEY) || '').trim();
  } catch (_) {
    return '';
  }
}

function readSalonUsername() {
  try {
    return String(sessionStorage.getItem(SALON_LOGIN_USERNAME_KEY) || '').trim();
  } catch (_) {
    return '';
  }
}

const LOGOUT_MAX_ATTEMPTS = 3;
const LOGOUT_RETRY_BASE_MS = 1000;

function isLogoutRetryable(status, errorMessage) {
  if (!status) return true;
  if (status >= 500) return true;
  if (status === 408 || status === 429) return true;
  if (errorMessage && /timed out|network|fetch/i.test(errorMessage)) return true;
  return false;
}

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Tell the salon backend to release this login row (is_logged_in, session token, etc.).
 * Sends token and username when available so stale tokens can still clear the DB row.
 * @returns {Promise<{ ok: boolean, alreadyLoggedOut?: boolean, noCredentials?: boolean, error?: string, status?: number }>}
 */
export async function logoutSalonSessionOnServer() {
  const loginSessionToken = String(sessionStorage.getItem(SALON_LOGIN_SESSION_TOKEN_KEY) || '').trim();
  const username = readSalonUsername();
  const deviceId = readSalonDeviceId();

  if (!loginSessionToken && !username) {
    return { ok: true, alreadyLoggedOut: true, noCredentials: true };
  }

  const payload = {};
  if (loginSessionToken) payload.login_session_token = loginSessionToken;
  if (username) payload.username = username;
  if (deviceId) payload.device_id = deviceId;

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (loginSessionToken) headers['X-Salon-Login-Token'] = loginSessionToken;
  if (deviceId) headers['X-Salon-Device-Id'] = deviceId;

  const base = getApiBaseUrl();
  let lastError = 'Logout request failed.';

  for (let attempt = 0; attempt < LOGOUT_MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await delay(LOGOUT_RETRY_BASE_MS * attempt);
    }

    try {
      const response = await fetchWithTimeout(`${base}${SALON_API_PREFIX}/logout`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        timeoutMs: 15000,
        skipSalonAuthHeaders: true,
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result?.success !== false) {
        return { ok: true };
      }

      lastError = result?.error || `Logout failed (${response.status})`;
      if (!isLogoutRetryable(response.status, lastError)) {
        return { ok: false, status: response.status, error: lastError };
      }
    } catch (e) {
      lastError = e?.message || 'Logout request failed.';
      if (attempt === LOGOUT_MAX_ATTEMPTS - 1) {
        return { ok: false, error: lastError };
      }
    }
  }

  return { ok: false, error: lastError };
}

/**
 * End salon login on the server, then wipe local salon auth storage only after success.
 * @returns {Promise<{ ok: boolean, alreadyLoggedOut?: boolean, noCredentials?: boolean, error?: string, status?: number }>}
 */
export async function performSalonLogout() {
  const serverResult = await logoutSalonSessionOnServer();
  if (serverResult.ok) {
    clearSalonAuthStorage();
    clearSalonStorageScopes();
  }
  return serverResult;
}

/**
 * Idle-timeout logout: always clear local auth even when the server request fails,
 * so the device is not stuck in a half-logged-in state.
 */
export async function performSalonIdleLogout() {
  const serverResult = await logoutSalonSessionOnServer();
  clearSalonAuthStorage();
  clearSalonStorageScopes();
  return { ...serverResult, localCleared: true };
}

/**
 * Validate salon login session with backend (checks is_active, expiry, device).
 * @returns {Promise<{ ok: boolean, code?: string, error?: string }>}
 */
export async function validateSalonLoginWithServer() {
  const loginSessionToken = String(sessionStorage.getItem(SALON_LOGIN_SESSION_TOKEN_KEY) || '').trim();
  const deviceId = String(sessionStorage.getItem(SALON_LOGIN_DEVICE_ID_KEY) || '').trim();
  if (!loginSessionToken || !deviceId) {
    return { ok: false, code: 'MISSING_SESSION' };
  }

  if (isSalonSessionIdleExpired()) {
    return { ok: false, code: 'IDLE_TIMEOUT', error: 'Session timed out due to inactivity.' };
  }

  try {
    const base = getApiBaseUrl();
    const response = await fetchWithTimeout(`${base}${SALON_API_PREFIX}/auth/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Salon-Login-Token': loginSessionToken,
        'X-Salon-Device-Id': deviceId,
      },
      body: JSON.stringify({ login_session_token: loginSessionToken, device_id: deviceId }),
      timeoutMs: 15000,
      skipSalonAuthHeaders: true,
    });
    const result = await response.json().catch(() => ({}));
    if (response.ok && result?.success) {
      if (result?.timeout_minutes) {
        setSalonLoginTimeoutMinutes(result.timeout_minutes);
      }
      if (result?.session_expires_at) {
        applyServerSessionExpiry(result.session_expires_at);
      } else {
        recordSalonUserActivity();
      }
      markSalonLoginServerValidated();
      return { ok: true };
    }
    return {
      ok: false,
      code: result?.code || 'INVALID_SESSION',
      error: result?.error || 'Login session is no longer valid.',
    };
  } catch (e) {
    return { ok: false, code: 'VALIDATE_FAILED', error: e?.message || 'Could not validate login session.' };
  }
}
